<template>
  <Accordion #default="{ isOpen, handleFocus }">
    <AccordionSection
      :path="'Hillcrest Hall 1.mp3'"
      :open="isOpen == 0"
      @open="handleFocus(0)"
    >
      <template #title>
        Hillcrest’s History
      </template>
      <template #content>
        <p v-if="captions" class="content">
          Hello and welcome to Hillcrest Hall. Opened in 1940, this building
          served as the first official women’s dormitory on Virginia Tech's
          campus. When it first opened, it housed up to 100 women, most of whom
          majored in Home Economics. Until 1971, the building operated as a
          women’s dorm and was known as the “skirt barn” among male students.
          Since, Hillcrest has operated as a men's athletic dorm, graduate
          housing, and is currently home to Virginia Tech’s honors college.
        </p>
        <Carousel :images="openingPhotos" />
      </template>
    </AccordionSection>
    <AccordionSection
      :path="'Hillcrest Hall 2.mp3'"
      :open="isOpen == 1"
      @open="handleFocus(1)"
    >
      <template #title>
        Black Women at Hillcrest
      </template>
      <template #content>
        <p v-if="captions" class="content">
          In regards to Black history, Hillcrest housed several of the first
          Black women to attend Virginia Tech. In 1966, the first cohort of
          Black women students were admitted to Virginia Tech. However, unlike
          their male counterparts the decade prior, these women were permitted
          to live on campus. Two of them, Linda Edmonds Turner and Freddie
          Hairston, lived right here in Hillcrest. The other four: Linda Adams
          Hoyle, Marguerite L. Harper, Jacquelyn “Jackie” Butler, and Chiquita
          Hudson lived in Eggleston Hall, which had been converted from a male
          to female dormitory in 1963. However, despite now living among their
          white peers on campus, racial segregation in housing remained. Each of
          these Black women lived with one of the others. In fact, Linda Adams
          Hoyle was originally assigned a white roommate, but was later
          reassigned after the white student's parents complained that their
          daughter would have to live with a Black student.
        </p>
        <Carousel :images="firstWomen" />
      </template>
    </AccordionSection>
    <AccordionSection
      :path="'Hillcrest Hall 3.mp3'"
      :open="isOpen == 2"
      @open="handleFocus(2)"
    >
      <template #title>
        Friendships and Community
      </template>
      <template #content>
        <ImageComponent :image="hudsonHarperAdams" />
        <p v-if="captions" class="content">
          Much like the Black male students of the 1950s, these women formed
          close bonds with each other, something that was almost inevitable
          given that Virginia Tech intentionally paired them. Mirroring the
          previous decade, these women also formed meaningful relationships with
          Black workers at Virginia Tech and in Black residents in the
          community.
        </p>
        <p v-if="captions" class="content">
          Although now more physically part of campus rather than town, black
          students continued to attend dances, church gatherings, and other
          social events hosted by black families in the community. Many students
          were introduced into the black community through connections and
          friendships they made with black employees of Virginia Tech. Linda
          Edmonds Turner and Freddie Hairston fondly remember the support they
          received from black cooks who worked in their dorm building. They
          would save Linda and Freddie’s favorite dishes and even cooked special
          meals for Freddie after she injured her teeth in a game of football.
          Jackie Blackwell and Linda Adams Hoyle both remember close friendships
          with several black maids in their dorm, including Belle Snell, whose
          family remained closely connected with black students for years
          afterwards. When recalling her relationship with another black maid,
          named Mrs. Jackson, Hoyle described her as “extended family” and as a
          “mother figure”.
        </p>
        <ImageComponent :image="higginsTurnerHarper" />
        <p v-if="captions" class="content">
          Overall, these women remember their time at Virginia Tech fondly.
          While their race certainly was a major factor in their individual
          experience, most recalled that they got along well with their white
          classmates. Much like a regular college experience, they were close
          with some and not with others. While living in Hillcrest Hall, Linda
          Edmonds recalls that she and her roommate, Freddie Hairston, were
          immediately part of the social culture of the dorm.
        </p>
        <ImageComponent :image="turner" />
        <p v-if="captions" class="content">
          However, there were still many girls in the dorm that kept their
          distance, and made no effort to befriend the pair. When parents would
          visit, students who had previously been friendly would often act as if
          they didn’t know the pair, and parents would often ask Linda and
          Freddie where the paper towels were, assuming that they were hired
          help. However, these women and other Black students did not allow such
          treatment to stop them from making the most of their undergraduate
          experience, with many Black students joining historically white clubs
          and organizations.
        </p>
        <hr />
        <p class="content pad-top">
          <b>Linda Edmonds Turner</b>
        </p>
        <AudioBar :path="'LindaEdmondsTurnerQuote.mp3'" :startTime="0" />
        <div class="pad-top">
          <p v-if="captions" class="content">
            <i>
              “I remember I would ask this white girl from my hometown, we'd be
              in classes, "In your high school did you do this or that?" And
              she'd go, "Oh--" She didn't want to talk to me, but as the years
              went by, she would talk more, but it was like she was always
              judging whether she should do it or not...There were some of the
              girls that were just truly friendly all the time; some just
              ignored you totally. Most of them just kind of ignored you. You
              were invisible in a way. The boys saw you. They looked, and they
              didn't look. There wasn't this direct confrontation. It was kind
              of like you weren’t there.”
            </i>
          </p>
          <Carousel :images="lindaEdmondsTurner" />
        </div>
        <hr />
        <p class="content pad-top">
          <b>Freddie Hairston</b>
        </p>
        <AudioBar :path="'FreddieHairstonQuote.mp3'" :startTime="0" />
        <div class="pad-top">
          <p v-if="captions" class="content">
            <i
              >“I felt more strangulated in terms of ideas. It was a more
              conservative school, and that was really hard for me, and I think
              in some ways this was really the place I encountered racism more
              inadvertently than any place else in my life up to that point-on a
              day-to-day basis. ...In classrooms and stuff, people who really
              didn't want to be near you. People who overtly would move away...
              or if you were in class you would see professors look at you in
              sort of exasperation. "What are you doing here?" The unspoken
              question would be that more so than anything else would.”</i
            >
          </p>
          <Carousel :images="freddieHairston" class="pad-top" />
        </div>
        <hr />
        <p class="content pad-top">
          <b>Linda Adams</b>
        </p>
        <div class="pad-top">
          <p v-if="captions" class="content">
            <i
              >“I think an atmosphere in which we weren't--that kept us
              conscious that we were black students on a white campus. Where it
              would be the norm and not the novelty environment to be in? I mean
              there were a lot of very nice students here. I studied with them.
              We talked. So it wasn't like everybody was against us, no. There
              were those we got along with, and we did things together, and
              others went their way. I guess there were just those that left us
              alone... I can remember a lot of them still being friendly,
              speaking: How are you doing? and you have conversations. But I
              think otherwise it's kind of hard to think of what all would be
              different other than the fact that it would be nice...we would
              really be talking about full integration having already existed
              rather than coming over and being the handful and being sort of,
              for lack of a better word, a novelty.”</i
            >
          </p>
        </div>
        <Carousel :images="lindaAdams" class="pad-top" />
        <hr />
        <p class="content pad-top">
          <b>Marguerite Harper Scott</b>
        </p>
        <AudioBar :path="'MargueriteHarperScottQuote.mp3'" :startTime="0" />
        <div class="pad-top">
          <p v-if="captions" class="content">
            <i
              >“In general, I think there was basically a kind of indifference.
              I don't remember them being particularly friendly or not friendly.
              People who lived beside you maybe, girls who might have been in
              some of your classes, you tended to be more friendly with maybe
              than other people. And again, come the weekend and it was time to
              find a party or something, then there was this little wall that
              separated us. They went their way, and you went your way.”</i
            >
          </p>
          <Carousel :images="margueriteHarperScott" class="pad-top" />
        </div>
        <hr />
        <p class="content pad-top">
          <b>Jackie Butler Blackwell</b>
        </p>
        <AudioBar :path="'JackieButlerBlackwellQuote.mp3'" :startTime="0" />
        <div class="pad-top">
          <p v-if="captions" class="content">
            <i
              >“But in the classroom sometimes when you go to class, and you
              were the only black person in the class, you would feel like
              everyone was staring at you. And I remember sometimes walking
              across the drillfield when it was cold, the first year I came up
              here--I think it snowed from October to May. It was so cold. I
              would think to myself sometimes that if I was walking to class
              between my dorm and the classroom, and I would faint here on this
              drillfield, no one would care. Everyone would just keep walking
              by. This was just some of the things that you would think about
              sometimes. You would wonder to yourself if anyone would stop or if
              they would just keep going. ...Basically, whenever I was sitting
              with students nearby, I would find out who they were, and
              sometimes we'd study together or things like that. I couldn't find
              any difference in the class than it would have been at any other
              school or in high school or in anything. The students, once they
              got to know you, were fine. At first, they might stare at you
              sometimes, but once you interacted with the students, then race or
              color wasn't important. It didn't seem like it mattered at all...A
              lot of times they had preconceived ideas. And as they got to know
              us, then they lost those ideas because they would ask us about
              certain things. We would tear down a lot of the stereotypes that
              they had had.”
            </i>
          </p>
          <Carousel :images="jackieButlerBlackwell" class="pad-top" />
        </div>
      </template>
    </AccordionSection>
  </Accordion>
</template>

<script>
import Accordion from "@/components/Accordion.vue";
import AccordionSection from "@/components/AccordionSection.vue";
import Carousel from "@/components/Carousel.vue";
import ImageComponent from "@/components/ImageComponent.vue";
import AudioBar from "@/components/AudioBar.vue";

export default {
  name: "HillcrestAccordion",
  components: {
    Accordion,
    AccordionSection,
    AudioBar,
    Carousel,
    ImageComponent
  },
  props: {
    captions: {
      type: Boolean
    }
  },
  data() {
    return {
      openingPhotos: [
        {
          name: "MILDRED TATE",
          path:
            "Opening Hillcrest Womens History Section/Copy of MILDRED TATE, PROFESSOR OF HOME ECONOMICS FROM 1937 THROUGH 1960.jpg",
          caption: "Mildred Tate: Dean of Women from 1937-1960."
        },
        {
          name: "Tate Letter",
          path:
            "Opening Hillcrest Womens History Section/MILDRED TATE LETTER, 1940.jpg",
          caption: "Mildred Tate's Letter regarding Hillcrest opening."
        },
        {
          name: "Hillcrest Hall",
          path:
            "Opening Hillcrest Womens History Section/Hillcrest Hall, 1941.jpg",
          caption: "Hillcrest 1941"
        },
        {
          name: "The Skirt Barn",
          path:
            "Opening Hillcrest Womens History Section/Hillcrest Hall, the Skirt Barn.jpeg",
          caption: "The Skirt Barn"
        },
        {
          name: "Students in home ec kitchen, 1950s-1960s",
          path:
            "Opening Hillcrest Womens History Section/Students in home ec kitchen, 1950s-60s.jpg",
          caption: "Students in home economics class, 1950s-1960s."
        },
        {
          name: "Skirt Barn, 1950.",
          path: "Opening Hillcrest Womens History Section/Skirt Barn, 1950.jpg",
          caption: "Skirt Barn, 1950."
        },
        {
          name: "Dr. Mildred Tate talking with female students, 1955",
          path:
            "Opening Hillcrest Womens History Section/Dr. Mildred Tate talking with female students, 1955.jpg",
          caption: "Dr. Mildred Tate talking with female students, 1955."
        },
        {
          name: "Dr. Laura Jane Harper, first dean of women",
          path:
            "Opening Hillcrest Womens History Section/Dr. Laura Jane Harper, first dean of women.jpg",
          caption: "Dr. Laura Jane Harper, first dean of women."
        }
      ],
      firstWomen: [
        {
          name: "Linda T",
          path: "Photos of first black women/Linda Edmonds Turner.png",
          caption: "Linda Edmonds Turner."
        },
        {
          name: "Freddie",
          path: "Photos of first black women/Freddie Hairston, 1968.jpg",
          caption: "Freddie Hairston, 1968."
        },
        {
          name: "Linda A",
          path: "Photos of first black women/Linda Adams, 1968.jpg",
          caption: "Linda Adams, 1968."
        },
        {
          name: "Marguerite",
          path: "Photos of first black women/Marguerite Harper Scott.png",
          caption: "Marguerite Harper Scott."
        },
        {
          name: "Jackie",
          path: "Photos of first black women/Jackie Butler 1969.jpg",
          caption: "Jackie Butler, 1969."
        },
        {
          name: "Chiquita",
          path:
            "Photos of first black women/La Verne Hairston, Linda Adams, and Chiquita Hudson - 1966.jpg",
          caption: "La Verne Hairston, Linda Adams, and Chiquita Hudson - 1966."
        }
      ],
      hudsonHarperAdams: {
        name: "hudson-harper-adams",
        path:
          "Photos of first black women/Chiquita Hudson, Marguerite Harper and Linda Adams, 1966 .jpg",
        caption: "Chiquita Hudson, Marguerite Harper and Linda Adams, 1966"
      },
      higginsTurnerHarper: {
        name: "higgins-harper-turner",
        path:
          "Photos of first black women/LA VERNE HIGGINS, LINDA EDMONDS TURNER, AND MARGUERITE LAURETTE (HARPER) _ CHICK_ SCOTT AT THE BLACK ALUMNI REUNION AWARDS CEREMONY, 2016.jpg",
        caption:
          "La Verne Higgins, Linda Edmonds Turner, and Marguerite Laurette (Harper) at the black alumni reunion awards ceremony, 2016"
      },
      turner: {
        name: "Turner",
        path: "Linda Edmonds Turner/Linda Edmonds Turner (1).png",
        caption: `"There were pajama parties. We'd go; they might not have wanted us to, but we were there. We never considered ourselves uninvited." -Linda Edmonds Turner`
      },
      lindaEdmondsTurner: [
        {
          name: "Linda Edmonds Turner",
          path: "Linda Edmonds Turner/Linda Edmonds Turner (1).png",
          caption: "Linda Edmonds Turner"
        },
        {
          name: "Post Graduation",
          path:
            "Photos of first black women/LA VERNE HIGGINS, LINDA EDMONDS TURNER, AND MARGUERITE LAURETTE (HARPER) _ CHICK_ SCOTT AT THE BLACK ALUMNI REUNION AWARDS CEREMONY, 2016.jpg",
          caption:
            "In addition to her two degrees from Virginia Tech, Turner holds a masters degree from Michigan State University and completed a postdoctoral fellowship in higher education administration at Harvard. Dr. Turner has held two college presidencies and executive positions at three fortune 500 companies. In 2015, she was named director of the Massachusetts Department of Industrial Accidents. "
        },
        {
          name: "Photo 3",
          path:
            "Photos of first black women/Linda Edmonds Turner in phi upsilon omicron, the choir.png",
          caption:
            "Pictured in this photo is Linda Edmonds Turner in the Phi Upsilon Omicron Choir at Virginia Tech. As you can see, Linda was the only non-white member of the group, but she did not allow her race to prevent her from becoming involved in student activities. Linda was also a member of the University Choir alongside Freddie Hairston. "
        },
        {
          name: "Photo 4",
          path:
            "Linda Edmonds Turner/Linda Edmonds in university home ec club.png",
          caption:
            "Linda was an active member of the University Home Economics Club. As you can see, Linda was the only Black member, and served as the club's recording secretary in 1970. "
        }
      ],
      freddieHairston: [
        {
          name: "Freddie Hairston",
          path: "Freddie Hairston/Freddie Hairston, 1968 (1).jpg",
          caption: "Freddie Hairston"
        },
        {
          name: "Post Graduation",
          path: "Freddie Hairston/Screen Shot 2020-10-28 at 9.53.39 AM.png",
          caption:
            "At the end of her sophomore year in 1969, Freddie left Virginia Tech after marrying Tech Junior Robert Clegg, a white civil rights activist. The couple moved to Minnesota, where Freddie would go on to earn a bachelor's degree in child psychology, an MBA. After the couple split, Freddie went on to earn her Doctorate from Oregon. Since, she has served as the director of a children’s agency and as professor at a Jesuit school in New York."
        },
        {
          name: "Photo 1",
          path: "Freddie Hairston/fredi papermate.gif",
          caption: `This Roanoke VA Native came to Virginia Tech in 1966 and immediately became involved at the university. In addition to joining the university choir, she also served on the news staff of The Virginia Tech. While on staff, She and Larry Billion (class of 1969) wrote "Back Talk," a column which dealt with controversial issues and presented both sides of the topics. Topics included the Vietnam War, Mixed Race Dating, the military draft, and labor unions.`
        },
        {
          name: "Photo 2",
          path: "Freddie Hairston/freddie at hillcrest dorm party.png",
          caption:
            "Despite being one of only two black women living in Hillcrest, Freddie did not allow that to stop her from participating in dorm events. Here you can see Freddie alongside several other Hillcrest residents at one of their dorm parties. "
        },
        {
          name: "Photo 3",
          path: "Freddie Hairston/freddie dress 1.png",
          caption: "Freddie in a dress."
        },
        {
          name: "Photo 4",
          path: "Freddie Hairston/freddie dress 2.png",
          caption: "Freddie in a dress."
        }
      ],
      lindaAdams: [
        {
          name: "Linda Adams",
          path: "Linda Adams/Linda Adams, 1968 (1).jpg",
          caption: "Linda Adams"
        },
        {
          name: "Description",
          path: "Linda Adams/Screen Shot 2020-10-28 at 10.05.17 AM.png",
          caption:
            "Linda Adams was the first official Black female graduate of Virginia Tech. She transferred to VPI, unlike her fellow Black students, and confronted intolerance as soon as she set foot on campus. She was given a white roommate, whose parents complained that their daughter shouldn’t have to room with a Black student. Despite the abrupt racism she faced and stares she endured as a Black female student, she became involved in campus life. Adams was the chair of the West Eggleston Disciplinary Committee, and organized the House Councils in the Eggleston halls. Though Eggleston is where she first experienced blatant intolerance, she rose up and became a leader, not being afraid to make her voice heard in the very place it was ignored. "
        },
        {
          name: "Photo",
          path: "Linda Adams/1967 unveristy chior.png",
          caption:
            "In the fall of 1966, a new 180-voice University choir began made up of students, faculty, and local Blacksburg residents. Both Freddie Hairston and Linda Edomonds Turner joined their freshman year. Here they are pictured. "
        },
        {
          name: "Photo",
          path: "Linda Adams/adams first graduate letter.jpg",
          caption:
            "In 1968, Linda Adams became the first Black woman to graduate from Virginia Tech. Here is a letter written by Dean of Women, Martha Harder, congratulating her for her success at Virginia Tech, both in the classroom and in her extracurriculars. Following graduation, Adams went on to work with the US Department of Commerces Bureau of the Census for almost 34 years before retiring. "
        }
      ],
      margueriteHarperScott: [
        {
          name: "Marguerite Harper Scott",
          path: "Marguerite Harper Scott/Marguerite Harper Scott (1).png",
          caption: "Marguerite Harper Scott"
        },
        {
          name: "Photo 1",
          path:
            "Marguerite Harper Scott/MARGUERITE HARPER, MEMBER OF STANDING SENATE COMMITTEE, CREDENTIALS AND ELECTIONS, THE 1969 BUGLE (2).jpg",
          caption:
            "In 1969, Scott was elected to the Student Government Association and served on the Standing Senate Committee."
        },
        {
          name: "Photo 2",
          path: "Marguerite Harper Scott/Dixie article 1.jpg",
          caption:
            "Scott was also an active member of the University’s Human Relations Council, an organization established to help improve the college experience for Black students, and improve relations between Black students and the administration. While Scott was a student, she pushed the University to discontinue the use of the Confederate Flag and the playing of Dixie after every Virginia Tech touchdown. This push sparked controversy and debates among the student body as you can see from the article here pictured. It would not be until the early 70s that the Flag and Dixie would be removed, only after a request by the football coach because he believed the practices were hurting recruitment. "
        }
      ],
      jackieButlerBlackwell: [
        {
          name: "Jackie Butler Blackwell",
          path: "Jackie Butler Blackwell/Jacki Butler 1969 (1).jpg",
          caption: "Jackie Butler Blackwell"
        },
        {
          name: "Photo 1",
          path:
            "Jackie Butler Blackwell/Jackie Butler Premed club, 1969 (1).jpg",
          caption:
            "While a student, Blackwell became involved in several extracurricular clubs. Here, you can see her pictured in the biology pre-med club. She was also a member of Angel Flight, which was a (more social) sister club to the Air Force ROTC program at VPI. Through this club, she was able to participate in social gatherings every Thursday in the elite upper-quad where civilians were not normally allowed. These gatherings were mixed-race, tolerant gatherings where they ate with their brother club. By participating in VPI’s integrated extracurricular activities, she helped break down social barriers on campus."
        }
      ]
    };
  }
};
</script>

<style>
.pad-top {
  padding-top: 2vw;
}
</style>
